import * as React from 'react'
import { clsx } from 'clsx'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Input } from '~/sotano-kit/primitives/input'
import * as PropertiesPanel from '../properties-panel'
import { useEditorStore } from '../editor-store'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { DocumentSidebarPortal } from '../document-sidebar-portal'
import { useDraggable } from '@dnd-kit/core'
import {
  isListBlock,
  Block,
  isCommentBlock,
  isDocumentBlock,
  isComponentBlock,
  isInstanceBlock,
  isReferenceBlock,
} from '@basehub/data/replicache/index'
import { useEditorWidth } from '~/hooks/use-editor-width'
import {
  DroppableBottomZone,
  DroppableTopZone,
  editorDndControllers,
  referenceDndControllers,
} from './document/dnd'
import { BlockIcon } from '~/components/block-icon'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { Portal } from '~/components/portal'
import { useEditorDropAssets } from '~/hooks/use-editor-drop-assets'
import { BlockVariant } from '.'
import { EditorModeProvider, useEditorMode } from '../editor-mode-provider'
import { useIsEditorOrHigher } from '~/hooks-data/use-user-role'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { createStore, useStore } from 'zustand'
import { useRepoStore } from '../repo-store'
import { SetOptional } from 'type-fest'
import { ReferenceInputStoreContext } from './reference/input'
import { useSelectedVariants } from '~/hooks/use-selected-variants'

import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { useTabsStore } from '../tabs/store'
import { useSearchParams } from 'next/navigation'
import { WorkflowRunControls } from './block-wrapper-actions/workflow-run-controls'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { Cross2Icon } from '@radix-ui/react-icons'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'

// -------- -------
// Lazy imports
// -------- -------

const BlockActions = React.lazy(
  () => import('./block-wrapper-actions/block-actions')
)

const OthersInBlock = React.lazy(
  () => import('./block-wrapper-actions/others-in-block')
)

const BlockConstraints = React.lazy(
  () => import('./block-wrapper-actions/constraints')
)

const CommentsInBlockWrapper = React.lazy(() =>
  import('./block-wrapper-actions/comments-in-block').then((mod) => ({
    default: mod.CommentsInBlockWrapper,
  }))
)

const RightActionsContainer = ({
  block,
  variant,
  children,
  isHovered,
}: {
  block: Block
  variant: BlockVariant
  children?: React.ReactNode
  isHovered: boolean
}) => {
  return (
    <div
      className={clsx(
        'absolute z-20 flex items-start gap-1',
        block.type === 'comment' ? 'top-[-4.5px]' : 'top-[6px]',
        variant === 'nested-union'
          ? 'left-[calc(100%-72px)]'
          : 'left-[calc(100%-82px)]',
        isHovered ? '!visible !opacity-100' : 'invisible opacity-0'
      )}
    >
      {children}
    </div>
  )
}

const VariantPills = React.lazy(() => import('./variant-pills'))

const BlockWrapperContextMenu = React.lazy(
  () => import('./block-wrapper-actions/context-menu')
)

const BlockLabelDropdownMenu = React.lazy(
  () => import('./block-wrapper-actions/dropdown-menu')
)

/* -------------------------------------------------------------------------------------------------
 * Block Wrapper
 * -----------------------------------------------------------------------------------------------*/

type BlockWrapperStore = {
  blockId: string
  selector?: string
  scopeRef: React.RefObject<HTMLDivElement | null>
  isDndDisabled: boolean
  isTouched: boolean
  variant: BlockVariant
  setBlockId: (blockId: string) => void
  setSelector: (selector?: string) => void
  setScopeRef: (scopeRef: React.RefObject<HTMLDivElement | null>) => void
  setIsDndDisabled: (isDndDisabled: boolean) => void
  setIsTouched: (isTouched: boolean) => void
  getSelectableElement: (
    scope?: HTMLDivElement | Document
  ) => HTMLDivElement | null
  setVariant: (variant: BlockVariant) => void
}

export const createBlockWrapperStore = (
  initialState?: Partial<BlockWrapperStore>
) => {
  return createStore<BlockWrapperStore>()((set, get) => ({
    blockId: '',
    selector: undefined,
    scopeRef: { current: null },
    isDndDisabled: false,
    isTouched: false,
    variant: 'default',
    setBlockId: (blockId) => set({ blockId }),
    setSelector: (selector) => set({ selector }),
    setScopeRef: (scopeRef) => set({ scopeRef }),
    setIsDndDisabled: (isDndDisabled) => set({ isDndDisabled }),
    setIsTouched: (isTouched) => set({ isTouched }),
    setVariant: (variant) => set({ variant }),
    getSelectableElement: (scope) => {
      const state = get()
      scope ??= state.scopeRef?.current || document
      if (!state.selector) return null
      return scope.querySelector<HTMLDivElement>(state.selector)
    },
    ...initialState,
  }))
}

export const BlockWrapperStoreContext = React.createContext<ReturnType<
  typeof createBlockWrapperStore
> | null>(null)

export const useBlockWrapperStore = <T,>(
  selector: (state: BlockWrapperStore) => T
): T => {
  const store = React.useContext(BlockWrapperStoreContext)
  if (!store) {
    throw new Error('Missing BlockWrapperStoreContext.Provider in the tree')
  }
  return useStore(store, selector)
}

export const BlockWrapper = ({
  block,
  parentBlock,
  path,
  children,
  focusableElementSelector = 'label',
  className,
  actionsClassName,
  contentClassName,
  noSelectOnClick,
  index,
  presenceTop = 16,
  variant = 'default',
}: {
  block: Block
  path: string
  children: React.ReactNode
  focusableElementSelector: string
  className?: string
  actionsClassName?: string
  contentClassName?: string
  noSelectOnClick?: boolean
  index: number
  presenceTop?: React.CSSProperties['top']
  parentBlock: Block
  variant?: BlockVariant
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const editorWidth = useEditorWidth()
  const sourceBlockId = idToSourceBlockId(block)
  const { isDragging, setIsDragging, onDrop } = useEditorDropAssets({
    block,
    path,
    parentBlock,
  })
  const recentlyCreatedBlocks = useWorkingTreeStore(
    (s) => s.recentlyCreatedBlocks
  )
  const removeRecentlyCreatedBlock = useWorkingTreeStore(
    (s) => s.removeRecentlyCreatedBlock
  )
  const editorMode = useEditorMode()

  const blockWrapperStore = React.useMemo(() => {
    // eslint-disable-next-line react-compiler/react-compiler
    return createBlockWrapperStore({
      scopeRef: contentRef,
      selector: focusableElementSelector,
      blockId: block.id,
      isTouched: false,
      variant,
    })
  }, [block.id, focusableElementSelector, variant])

  const referenceStore = React.useContext(ReferenceInputStoreContext)
  const setAddNewBlockPosition =
    referenceStore?.getState().setAddNewBlockPosition
  const onRemoveTarget = referenceStore?.getState().onRemoveTarget ?? null

  const isEditorOrHigher = useIsEditorOrHigher()
  const configureAccess =
    editorMode === 'write:all' ||
    ((isListBlock(parentBlock) || isReferenceBlock(parentBlock)) &&
      isEditorOrHigher)
  let showActions = configureAccess
  if (variant === 'nested-union') {
    showActions =
      showActions &&
      isReferenceBlock(parentBlock) &&
      (parentBlock.constraints.multiple ||
        parentBlock.value.targetBlockIds.length > 1)
  }

  const focusElement = React.useCallback(
    (toLabel = true, options?: FocusOptions): boolean => {
      const label = contentRef.current?.querySelector<HTMLLabelElement>('label')
      if (label && toLabel && configureAccess) {
        // trigger a dblclick event, ref: https://stackoverflow.com/a/18399339
        const dblClickEvent = new MouseEvent('dblclick', {
          view: window,
          bubbles: true,
          cancelable: true,
        })

        label.dispatchEvent(dblClickEvent)
        return true
      } else {
        if (contentRef.current) {
          const selectableElement = blockWrapperStore
            .getState()
            .getSelectableElement(contentRef.current)
          if (selectableElement) {
            selectableElement.focus(options)
            if (!options?.preventScroll) {
              setTimeout(() => {
                selectableElement.scrollIntoView({ block: 'nearest' })
              }, 100)
            }
            return true
          }
        }
      }
      return false
    },
    [blockWrapperStore, configureAccess]
  )

  const focus = useRepoStore((s) =>
    s.focus?.blockId === sourceBlockId ? s.focus : null
  )
  const setFocus = useRepoStore((s) => s.setFocus)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
  const setHoveredBlockId = useEditorStore((s) => s.setHoveredBlockId)

  React.useEffect(() => {
    if (!focus || focus.blockId !== sourceBlockId) return

    if (focus.type === 'block:wrapper') {
      setSelectedBlockId(sourceBlockId)
      const blockWrapperEl = contentRef.current
      if (blockWrapperEl) {
        setFocus(null)
        blockWrapperEl.focus({ preventScroll: focus.preventScroll })
        if (!focus.preventScroll) {
          setTimeout(() => {
            blockWrapperEl.scrollIntoView({ block: 'nearest' })
          }, 100)
        }
      }
    } else if (focus.type === 'block:name') {
      setIsRenamingBlockId(focus.blockId)
      setFocus(null)
    } else if (focus.type === 'block:value') {
      const successFocusing = focusElement(false, {
        preventScroll: focus.preventScroll,
      })
      if (successFocusing) {
        setFocus(null)
      } else {
        // leave it to some child to focus the element, or the next render or something.
      }
    }
  }, [
    focus,
    focusElement,
    setFocus,
    setIsRenamingBlockId,
    setSelectedBlockId,
    sourceBlockId,
  ])

  const hasRendered = useHasRendered()

  let dndDisabled = !configureAccess
  if (variant === 'nested') {
    dndDisabled = true
  }
  if (!hasRendered) {
    dndDisabled = true
  }

  const removeDropIndicator: React.DragEventHandler = (e) => {
    e.preventDefault()
    const area = e.target as HTMLDivElement | null
    const indicator = area?.firstChild
    if (!indicator) return
    ;(indicator as HTMLDivElement).classList.remove('bg-dark-control-base')
  }

  const addDropIndicator: React.DragEventHandler = (e) => {
    e.preventDefault()
    if (!e.dataTransfer.types.includes('Files')) return

    const area = e.target as HTMLDivElement | null
    const indicator = area?.firstChild
    if (!indicator) return
    ;(indicator as HTMLDivElement).classList.add('bg-dark-control-base')
  }

  const isSelected = useEditorStore((s) => s.selectedBlockId === sourceBlockId)
  const isHovered = useEditorStore((s) => s.hoveredBlockId === block.id)
  const [wasHoveredAtLeastOnce, setWasHoveredAtLeastOnce] =
    React.useState(false)

  React.useEffect(() => {
    if (isHovered) {
      setWasHoveredAtLeastOnce(true)
    }
  }, [isHovered])
  const isCut = useRepoStore(
    (s) => !!s.cutBlocks?.ids.some((id) => id === block.id)
  )

  React.useEffect(() => {
    if (editorMode === 'preview') return
    if (isSelected && isCommentBlock(block)) {
      setFocus({
        blockId: sourceBlockId,
        type: 'block:value',
      })
    }
  }, [isSelected, block, setFocus, editorMode, sourceBlockId])

  React.useEffect(() => {
    blockWrapperStore.setState({ isDndDisabled: dndDisabled })
  }, [blockWrapperStore, dndDisabled])

  const searchParams = useSearchParams()
  const searchParamsCommentFocus = searchParams.get('commentId')

  return (
    <BlockWrapperStoreContext.Provider value={blockWrapperStore}>
      {variant === 'nested-union' && (
        <div id={`union-wrapper-portal-top-${block.id}`} />
      )}
      <React.Suspense fallback={null}>
        <EditorModeProvider block={block} parentBlock={parentBlock}>
          <BlockWrapperContextMenu
            block={block}
            path={path}
            parentBlock={parentBlock}
            canOpenPropertiesPanel
          >
            <div
              className={clsx(
                // opacity-100 because dnd-kit puts opacity: 0 onDragEnd, odly.
                'cursor-default !opacity-100',
                'relative scroll-my-10',
                variant === 'nested' ? 'w-full' : '-mx-24 px-24 py-2',
                className
              )}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes('Files')) return
                setIsDragging(true)
              }}
              onDrop={() => setIsDragging(false)}
              onBlur={(e) => {
                // if some child is blurred, we'll consider it as touched.
                if (e.currentTarget.contains(document.activeElement)) return
                blockWrapperStore.setState({ isTouched: true })
                if (recentlyCreatedBlocks.has(block.id)) {
                  removeRecentlyCreatedBlock(block.id)
                }
              }}
              onDragLeave={() => setIsDragging(false)}
              onPointerOver={(e) => {
                // i removed the dndDisabled check here because
                // i want to show the comments popover when the block is hovered.
                // //if (dndDisabled) return
                e.stopPropagation()
                setHoveredBlockId(block.id)
              }}
              onPointerLeave={(e) => {
                // // if (dndDisabled) return
                e.stopPropagation()
                setHoveredBlockId(null)
              }}
            >
              {!dndDisabled && (
                <>
                  {/* Droppable areas for dropping files & creating new blocks */}
                  <div
                    style={{ width: editorWidth }}
                    className={clsx(
                      isDragging && 'z-20',
                      'absolute top-0 left-1/2 h-1/2 -translate-x-1/2 px-24'
                    )}
                    onDrop={(e) => {
                      removeDropIndicator(e)
                      onDrop(e, 'before')
                    }}
                    onDragOver={addDropIndicator}
                    onDragLeave={removeDropIndicator}
                  >
                    <div className="pointer-events-none absolute mx-auto max-w-[calc(1024px-96px_*_2)] -top-px left-24 right-24 h-0.5 select-none rounded-md" />
                  </div>
                  <div
                    style={{ width: editorWidth }}
                    className={clsx(
                      isDragging && 'z-20',
                      'absolute top-1/2 left-1/2 h-1/2 -translate-x-1/2 px-24'
                    )}
                    onDrop={(e) => {
                      removeDropIndicator(e)
                      onDrop(e, 'after')
                    }}
                    onDragOver={addDropIndicator}
                    onDragLeave={removeDropIndicator}
                  >
                    <div className="pointer-events-none absolute -bottom-px h-0.5 mx-auto max-w-[calc(1024px-96px_*_2)] left-24 right-24 select-none rounded-md" />
                  </div>
                </>
              )}

              {/* Presence */}
              <React.Suspense fallback={null}>
                <OthersInBlock blockId={block.id} top={presenceTop} />
              </React.Suspense>

              {/* Comments */}
              {variant !== 'nested' &&
                editorMode !== 'preview' &&
                (wasHoveredAtLeastOnce || !!searchParamsCommentFocus) && (
                  <React.Suspense>
                    <RightActionsContainer
                      isHovered={isHovered}
                      block={block}
                      variant={variant}
                    >
                      {onRemoveTarget && (
                        <div className="flex transform translate-y-[14px]">
                          <SimpleTooltip content="Remove reference">
                            <IconButton
                              onClick={() =>
                                onRemoveTarget(idToSourceBlockId(block))
                              }
                              className="!size-6"
                            >
                              <Cross2Icon className="size-[18px] text-dark-muted" />
                            </IconButton>
                          </SimpleTooltip>
                        </div>
                      )}
                      <CommentsInBlockWrapper
                        block={block}
                        isHovered={isHovered}
                      />
                    </RightActionsContainer>
                  </React.Suspense>
                )}
              {variant !== 'nested' && (
                <div>
                  {showActions && (
                    <React.Suspense fallback={null}>
                      <BlockActions
                        wrapperClassName={clsx(
                          'absolute top-[19px] z-10 flex',
                          variant === 'nested-union'
                            ? 'right-[calc(100%-80px)]'
                            : 'right-[calc(100%-86px)]',
                          actionsClassName
                        )}
                        block={block}
                        path={path}
                        parentBlock={parentBlock}
                        isHovered={isHovered}
                        index={index}
                        dndDisabled={dndDisabled}
                        onAddNewBlock={
                          variant === 'nested-union'
                            ? (altKey) => {
                                setAddNewBlockPosition?.({
                                  id: block.id,
                                  type: altKey ? 'before' : 'after',
                                })
                              }
                            : undefined
                        }
                      />
                    </React.Suspense>
                  )}
                </div>
              )}

              <div
                className={clsx(
                  'rounded-xl border',
                  variant !== 'nested-union' && '-mx-2',
                  !isCommentBlock(block) ? 'p-2' : 'px-2',
                  (isDocumentBlock(block) ||
                    isComponentBlock(block) ||
                    isInstanceBlock(block)) &&
                    '-my-2',
                  'focus:border-dark-control-base focus:outline-none',
                  isSelected &&
                    !isCommentBlock(block) &&
                    editorMode !== 'preview'
                    ? "border-dark-control-base focus-within:border-dark-elevated [&:has([data-variant-item][data-active='true'])]:border-dark-elevated"
                    : 'border-transparent'
                )}
              >
                <div
                  className={clsx(
                    contentClassName,
                    // relative here is important to prevent droppable areas from overlapping the block content
                    'relative flex -m-2 px-2 !py-2 scroll-m-10 flex-col rounded-xl',
                    isCut && 'opacity-40'
                  )}
                  ref={contentRef}
                  onPointerDown={(e) => {
                    if (!noSelectOnClick) {
                      setSelectedBlockId(sourceBlockId)
                      e.stopPropagation()
                      if (
                        e.target instanceof HTMLElement &&
                        document.activeElement instanceof HTMLElement
                      ) {
                        const state = blockWrapperStore.getState()
                        const selectableElement = state.getSelectableElement()
                        if (selectableElement) {
                          if (
                            state.selector &&
                            (!e.target.closest(state.selector) ||
                              e.target !== selectableElement)
                          ) {
                            // prevent event if the click is NOT against the selectable element.
                            // also, blur the active element.
                            // e.preventDefault()
                            // document.activeElement.blur()
                          }
                        }
                      }
                    }
                  }}
                  data-block-id={sourceBlockId}
                >
                  {children}
                </div>
              </div>

              {!dndDisabled && (
                <>
                  <DroppableTopZone
                    block={block}
                    parentId={parentBlock.id}
                    index={index}
                  />
                  <DroppableBottomZone
                    block={block}
                    parentId={parentBlock.id}
                    index={index}
                  />
                </>
              )}
            </div>
          </BlockWrapperContextMenu>
        </EditorModeProvider>
      </React.Suspense>
      {variant === 'nested-union' && (
        <div id={`union-wrapper-portal-bottom-${block.id}`} />
      )}
    </BlockWrapperStoreContext.Provider>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Block Label
 * -----------------------------------------------------------------------------------------------*/

export const BlockLabel = ({
  block,
  parentBlock,
  path,
  index,
  moreActions,
  showActions = true,
  className,
  afterTitle,
  previewActions,
}: {
  block: Block
  parentBlock: Block
  path: string
  index: number
  moreActions?: React.ReactNode
  showActions?: boolean
  className?: string
  afterTitle?: React.ReactNode
  previewActions?: React.ReactNode
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const getSelectableElement = useBlockWrapperStore(
    (s) => s.getSelectableElement
  )
  const sourceBlockId = idToSourceBlockId(block)
  const isRenamingBlock = useEditorStore(
    (s) => s.isRenamingBlockId === sourceBlockId
  )
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const isSelected = useEditorStore((s) => s.selectedBlockId === sourceBlockId)
  const isHovered = useEditorStore((s) => s.hoveredBlockId === sourceBlockId)
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const undoManager = useRepoUndoManager()
  const editorMode = useEditorMode()

  const {
    onChange,
    sync: _,
    valueRef: __,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    customEventsKey: `synced-value:label:${sourceBlockId}`,
    value: block.title,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          path,
          id: block.id,
          data: {
            type: block.type,
            title: v,
          },
          instanceChildTarget: block.instanceChildTarget,
          strategy: 'merge',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:name',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:name',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        path,
        block.id,
        block.instanceChildTarget,
        block.type,
        block.sourceBlockId,
        undoManager,
        activeTabKey,
      ]
    ),
  })

  const selectedVariants = useSelectedVariants({ parentBlock, block })

  const configureAccess = editorMode === 'write:all'
  if (isRenamingBlock && !configureAccess) {
    throw new Error('cannot rename block in read-only mode')
  }

  return (
    <div
      className={clsx(
        'flex items-center justify-between pb-2 text-dark-muted',
        block.description ? 'min-h-14' : 'min-h-9',
        className
      )}
    >
      <div className="flex gap-2 items-start flex-1">
        <div className="h-5 py-0.5 flex items-center justify-between">
          <BlockIcon
            block={block}
            iconProps={{
              className: 'flex-shrink-0 text-dark-muted',
              width: 16,
              height: 16,
            }}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 justify-center items-start">
          <div className="flex items-center justify-between self-stretch !h-5">
            <div className="flex gap-2 items-center flex-1">
              {isRenamingBlock ? (
                <Input
                  type="text"
                  placeholder="Untitled"
                  onBlur={() => {
                    setIsRenamingBlockId(null)
                  }}
                  onFocus={(e) => {
                    e.currentTarget.select()
                    setSelectedBlockId(sourceBlockId)
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation()
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      e.preventDefault()
                      const selectableElement = getSelectableElement()
                      if (selectableElement) selectableElement.focus()
                      else e.currentTarget.blur()
                    }
                  }}
                  autoFocus
                  className="!-my-1 !h-6 basis-[300px] bg-dark-shade-hover !py-0.5 !px-1 text-xs leading-4 placeholder:text-dark-muted"
                  onChange={(e) => onChange(e.target.value)}
                  {...syncedInputProps}
                />
              ) : (
                <DraggableLabel
                  block={block}
                  parentBlock={parentBlock}
                  index={index}
                  tabIndex={-1}
                  className="max-w-[400px] cursor-default select-none overflow-hidden text-xs font-medium leading-4 flex items-center"
                  id={`label-${block.id}`}
                  htmlFor={block.id}
                  onDoubleClick={() => {
                    if (!configureAccess) return
                    setIsRenamingBlockId(sourceBlockId)
                  }}
                  onFocus={(e) => {
                    e.target.blur()
                  }}
                >
                  <span className="overflow-ellipsis whitespace-nowrap overflow-hidden">
                    {syncedInputProps.value || 'Untitled'}
                  </span>
                </DraggableLabel>
              )}

              {afterTitle}

              <div
                className="flex gap-2 items-center flex-1 select-none"
                style={{ display: isRenamingBlock ? 'none' : 'flex' }}
              >
                <React.Suspense fallback={null}>
                  <WorkflowRunControls
                    sourceBlockId={sourceBlockId}
                    instanceChildTarget={block.instanceChildTarget}
                  />

                  <VariantPills
                    variants={selectedVariants}
                    blockId={idToSourceBlockId(parentBlock)}
                  />

                  <BlockConstraints block={block} parentBlock={parentBlock} />
                </React.Suspense>
              </div>
            </div>
            {showActions && editorMode !== 'preview' && (
              <div className="flex gap-1 items-center">
                <div
                  id={`more-actions-visible-portal-${block.id}`}
                  className="flex h-5"
                />
                <div
                  className={clsx(
                    !isSelected &&
                      !isHovered &&
                      editorMode !== 'read' &&
                      'invisible opacity-0',
                    'flex gap-1 items-center'
                  )}
                >
                  <div
                    id={`more-actions-portal-${block.id}`}
                    className="flex max-h-5 items-center"
                  >
                    {moreActions}
                  </div>
                  <React.Suspense fallback={null}>
                    <BlockLabelDropdownMenu
                      block={block}
                      path={path}
                      parentBlock={parentBlock}
                    />
                  </React.Suspense>
                </div>
              </div>
            )}
            {/* For actions on hover  */}
            {previewActions && (
              <div className={clsx('flex gap-1')}>{previewActions}</div>
            )}
          </div>
          {block.description ? (
            <div className="flex items-center !min-h-5">
              <p className="text-xs text-dark-faint whitespace-pre-line">
                {block.description}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const DraggableLabel = ({
  block,
  parentBlock,
  index,
  ...rest
}: React.JSX.IntrinsicElements['label'] & {
  block: Block
  parentBlock: Block
  index: number
}) => {
  const dndDisabled = useBlockWrapperStore((s) => s.isDndDisabled)
  const variant = useBlockWrapperStore((s) => s.variant)

  const isDndEnabled = React.useDeferredValue(!dndDisabled)

  const draggable = useDraggable({
    id: `block-wrapper/label-${block.id}`,
    data: editorDndControllers.draggableDataSchema.parse({
      // for now, we'll only allow dragging one block at a time
      keysDragged: new Set([{ label: block.title, id: block.id }]),
      type:
        variant === 'nested-union'
          ? referenceDndControllers.dndType
          : editorDndControllers.dndType,
      index, // this index value accounts for only one item. BUT, when we have multi-dnd, we'll need to know each key's index.
      currentParentKey: parentBlock.id,
    }),
    disabled: !isDndEnabled,
  })

  return (
    <label
      {...rest}
      ref={!dndDisabled ? draggable.setNodeRef : undefined}
      {...(!dndDisabled ? draggable.attributes : undefined)}
      {...(!dndDisabled ? draggable.listeners : undefined)}
    />
  )
}

//
/* -------------------------------------------------------------------------------------------------
 * Block More Actions
 * -----------------------------------------------------------------------------------------------*/

export const BlockLabelMoreActionsPortal = ({
  children,
  blockId,
  visible,
  className,
}: {
  children: React.ReactNode
  blockId: string
  visible?: boolean
  className?: string
}) => {
  const hasRendered = useHasRendered()

  if (!hasRendered) return null
  return (
    <Portal
      id={`more-actions-${visible ? 'visible-portal' : 'portal'}-${blockId}`}
      className={className}
    >
      {children}
    </Portal>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Block Properties Panel Wrapper
 * -----------------------------------------------------------------------------------------------*/

export const BlockPropertiesPanelWrapper = ({
  block,
  children,
  path,
  parentBlock,
  blockTitleGroup,
  isFullView,
}: {
  block: Block
  children?: React.ReactNode
  path: string
  parentBlock: Block
  blockTitleGroup?:
    | false
    | SetOptional<PropertiesPanel.BlockTitleGroupProps, 'block' | 'path'>
  isFullView?: boolean
}) => {
  const sourceBlockId = idToSourceBlockId(block)
  const isSelected = useEditorStore(
    (s) =>
      s.selectedBlockId === sourceBlockId || (isFullView && !s.selectedBlockId)
  )
  const editorMode = useEditorMode()

  return (
    <DocumentSidebarPortal hidden={!isSelected || editorMode === 'preview'}>
      <PropertiesPanel.Root block={block} parentBlock={parentBlock}>
        {blockTitleGroup !== false && (
          <PropertiesPanel.BlockTitleGroup
            block={block}
            path={path}
            {...blockTitleGroup}
          />
        )}
        {children}
      </PropertiesPanel.Root>
    </DocumentSidebarPortal>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Helpers
 * -----------------------------------------------------------------------------------------------*/

export const useBlockInputBaseProps = (block: Block) => {
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const sourceBlockId = idToSourceBlockId(block)

  let userSelect = true
  let inputType: string | undefined = undefined
  switch (block.type) {
    case 'text':
    case 'number':
      inputType = block.type
      break
    case 'media':
      inputType = 'file'
      userSelect = false
      break
    case 'boolean':
      inputType = 'checkbox'
      break
    case 'date':
      inputType = block.typeOptions.useTime ? 'datetime-local' : 'date'
      break
    case 'color':
      inputType = 'color'
      break
    default:
      break
  }

  return {
    name: block.title,
    [`data-${block.type}-block`]: true,
    [`data-block-id`]: sourceBlockId,
    autoComplete: 'off',
    style: {
      userSelect: userSelect ? 'auto' : 'none',
    } satisfies React.CSSProperties,
    type: inputType,
    onFocus: () => {
      setSelectedBlockId(sourceBlockId)
    },
  }
}
